import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createFbSqUser,
  createFbSqUserFromUserCredential,
  getUserDataFromUserCredential,
  loadFbSqUser,
} from 'api/UserAPI';
import { ArmyGreenCls, CrimsonCls, Emojis, LocationCls, ValueCls } from 'constants/constants';
// import { resetSquares } from 'slices/SquaresSlice';
import { resetSquares } from 'slices/SquaresSlice';
import { AppThunk, RootState } from 'slices/store';
import { Maybe } from 'types/App.types';
import { FbSqUser } from 'types/SqGame.types';
import {
  auth,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  User,
  UserCredential,
} from '../firebase/Firebase';

export enum UserAuthState {
  Init = 'Init',
  Authenticating = 'Authenticating',
  LoggedIn = 'LoggedIn',
  NotLoggedIn = 'NotLoggedIn',
  LoggedOut = 'LoggedOut',
  PasswordReset = 'PasswordReset',
  Error = 'Error',
}

export interface SqUserProps {
  isLoading: boolean;
  authState: UserAuthState;
  isAuthStateObserverMounted: boolean;
  user: FbSqUser;
}

const initialState: SqUserProps = {
  isLoading: false,
  authState: UserAuthState.Init,
  isAuthStateObserverMounted: false,
  user: {
    uid: '',
    email: '',
    name: '',
    isAnonymous: false,
    emailVerified: false,
    isEditor: false,
    createdOn: 0,
    modifiedOn: 0,
  },
};

// #region USER ACCOUNT ACTIONS ***********************************************
export const doLogoutUser = (): AppThunk => (dispatch) => {
  dispatch(setUserAuthState(UserAuthState.LoggedOut));
  signOut(auth)
    .then(() => {
      console.log(`${Emojis.eyes} %c LOGOUT %c AH Success`, CrimsonCls, '');
      dispatch(resetSquares());
      dispatch(resetSqUser());
    })
    .catch((reason) => {
      console.log(`${Emojis.eyes} %c LOGOUT  %c, ${reason}`, CrimsonCls, '');
      // logEvent(analytics, 'exception', { description: `method: performUserLogout, reason: ${reason}` });
    });
};

export const doCreateAccount =
  (email: string, password: string, name: string): AppThunk =>
    (dispatch) => {
      dispatch(setUserAuthState(UserAuthState.Authenticating));
      // dispatch(setCreateAccountFormError(''));
      createUserWithEmailAndPassword(auth, email, password)
        .then(async (userModel: UserCredential) => {
          const newUser: FbSqUser = await createFbSqUserFromUserCredential({
            ...userModel,
            user: { ...userModel.user, displayName: name },
          });
          dispatch(setUserAuthState(UserAuthState.LoggedIn));
          dispatch(setFbSqUser(newUser));
          // dispatch(setToastNotification(getToastSuccessPayload('Account Created')));

          // TODO: @Andrey11 send account email verification
          // dispatch(doSendVerificationEmail());
        })
        .catch((reason) => {
          console.log(`performCreateAccount Error ${reason}`);
          dispatch(setUserAuthState(UserAuthState.Error));
          // logEvent(analytics, 'exception', { description: `method: performCreateAccount, reason: ${reason}` });
          // dispatch(setCreateAccountFormError(reason.code));
          // dispatch(setUserStatus('idle'));
        });
    };

export const doLoginWithEmailAndPassword =
  (email: string, password: string): AppThunk =>
    (dispatch) => {
      dispatch(setUserAuthState(UserAuthState.Authenticating));
      // dispatch(setLoginFormError(''));
      signInWithEmailAndPassword(auth, email, password)
        .then((userModel) => {
          const maybeEmail = userModel.user.email ?? 'no email';
          console.log(`${Emojis.checkMark} %c LOGGED IN %c %c ${maybeEmail} %c`, ArmyGreenCls, '', ValueCls, '');
        })
        .catch((reason) => {
          console.log(`${Emojis.warning} %c LOGIN ERROR %c`, CrimsonCls, '');
          console.log(`${Emojis.warning} %c ${reason.code} %c`, ValueCls, '');
          dispatch(setUserAuthState(UserAuthState.Error));
        });
    };

export const doResetUserPassword =
  (email: string): AppThunk =>
    (dispatch) => {
      sendPasswordResetEmail(auth, email)
        .then(() => {
          dispatch(setUserAuthState(UserAuthState.PasswordReset));
          console.log(`${Emojis.checkMark} %c RESET EMAIL SENT %c`, ArmyGreenCls, '');
        })
        .catch((reason) => {
          console.log(`${Emojis.warning} %c RESET EMAIL ERROR %c`, CrimsonCls, '');
          console.log(`${Emojis.warning} %c ${reason.code} %c`, ValueCls, '');
          dispatch(setUserAuthState(UserAuthState.Error));
        });
    };

export const subscribeUserStatusChange = (): AppThunk => (dispatch, getState) => {
  const isMounted = getState().users.isAuthStateObserverMounted;

  if (!isMounted) {
    dispatch(setIsAuthStateObserverMounted(true));
    dispatch(setUserAuthState(UserAuthState.Authenticating));

    auth.onAuthStateChanged(async (userModel: User | null) => {
      if (userModel) {
        const uid = userModel.uid;
        const verified = userModel.emailVerified;
        const verifiedStatus = `${verified ? '' : 'not '}verified`;
        const msg = `%c onAuthStateChanged %c %c ${uid} %c %c ${userModel.email} %c %c ${verifiedStatus} %c`;
        console.log(`${Emojis.eyes} ${msg}`, LocationCls, '', ValueCls, '', ValueCls, '', ValueCls, '');
        const usr = getUserDataFromUserCredential(userModel);

        /**
         *
        if (getState().user.userStatus === 'creatingAccount') {
          isPrint(PRINT_FLAG) && console.log(...printMsg('onAuthStateChanged', 'creatingAccount', 'exiting this flow'));
          return;
        }
        if (getState().ui.appUi.appStatus === AppStatus.Offline) {
          if (getState().user.userStatus === 'signingIn' && getState().ui.userUi.showLoginForm) {
            dispatch(setLoginFormError('app/offline'));
          }
          dispatch(setUserStatus('notSignedIn'));
          dispatch(setToastMessage({ message: 'App is OFFLINE', type: AppNotification.Warning }));
          dispatch(setShowToast(true));
          return;
        }
         */

        try {
          const user = await loadFbSqUser(uid);
          if (user) {
            if (user.emailVerified !== usr.emailVerified) {
              console.log(`${Emojis.eyes} Email verified status should be %c ${usr.emailVerified} %c`, ValueCls, '');
            } else {
              console.log(`${Emojis.eyes} Email verified status is %c ${user.emailVerified} %c`, ValueCls, '');
            }

            dispatch(setFbSqUser(user));
            dispatch(setUserAuthState(UserAuthState.LoggedIn));
          } else {
            dispatch(setUserAuthState(UserAuthState.NotLoggedIn));
          }
          // const userInfo: IUser = await fetchUserInfo(uid, userModel.emailVerified);
          // dispatch(setUserId(uid));
          // dispatch(setUserInfo(userInfo));
          // const profilesT = await fetchProfilesByUserId(uid);
          // if (profilesT) {
          //   dispatch(setUserProfiles(profilesT));
          //   const profileTitle = userInfo.activeProfileId ? getActiveProfileTitle(getState()) : userInfo.firstname;
          //   if (userInfo.activeProfileId) {
          //     const preferences = await getUserPreferences(userInfo.activeProfileId);
          //     dispatch(doSetUserPreferencesState({ preferences: preferences, status: 'loaded' }));
          //   }
          //   dispatch(setToastNotification(getToastSuccessPayload(`${profileTitle} signed in`)));
          // }
        } catch (error: unknown) {
          console.log(`Error has occurred: ${error}`);
          console.dir(error);

          if (uid && userModel.email && (error as Error).message === 'USER_NOT_ADDED') {
            const newUser: FbSqUser = await createFbSqUser(userModel);
            dispatch(setFbSqUser(newUser));
            dispatch(setUserAuthState(UserAuthState.LoggedIn));
          }
          // dispatch(performUserLogout());
        }
        // } else if (getUserStatus(getState()) === 'idle' || getUserStatus(getState()) === 'signingIn') {
        //   dispatch(setUserStatus('notSignedIn'));
        // } else {
        //   dispatch(setEditorState(false));
        //   dispatch(resetState());
      }
    });
  }
};

export const onUserNotLoggedIn = (): AppThunk => (dispatch) => {
  dispatch(setUserAuthState(UserAuthState.NotLoggedIn));
  // redirect(SqRoutes.Login);
};

export const sqUserSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setUserAuthState: (state, action: PayloadAction<UserAuthState>) => {
      state.authState = action.payload;
    },
    setIsAuthStateObserverMounted: (state, action: PayloadAction<boolean>) => {
      state.isAuthStateObserverMounted = action.payload;
    },
    setFbSqUser: (state, action: PayloadAction<FbSqUser>) => {
      state.user = action.payload;
    },
    setUid: (state, action: PayloadAction<string>) => {
      state.user.uid = action.payload;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.user.email = action.payload;
    },
    setName: (state, action: PayloadAction<string>) => {
      state.user.name = action.payload;
    },
    setEmailVerified: (state, action: PayloadAction<boolean>) => {
      state.user.emailVerified = action.payload;
    },
    setIsAnonymousUser: (state, action: PayloadAction<boolean>) => {
      state.user.isAnonymous = action.payload;
    },
    setIsEditor: (state, action: PayloadAction<boolean>) => {
      state.user.isEditor = action.payload;
    },
    setCreatedOn: (state, action: PayloadAction<number>) => {
      state.user.createdOn = action.payload;
    },
    setModifiedOn: (state, action: PayloadAction<number>) => {
      state.user.modifiedOn = action.payload;
    },

    // RESET ******************************************************************
    resetSqUser: (state) => {
      return {
        ...initialState,
        isAuthStateObserverMounted: state.isAuthStateObserverMounted,
        authState: state.authState,
      };
    },
  },
});

export const {
  // USER
  setIsLoading,
  setUserAuthState,
  setIsAuthStateObserverMounted,
  setFbSqUser,
  setUid,
  setEmail,
  setName,
  setEmailVerified,
  setIsAnonymousUser,
  setIsEditor,
  setCreatedOn,
  setModifiedOn,

  resetSqUser,
} = sqUserSlice.actions;

export const getIsLoading = (state: RootState): boolean => state.users.isLoading;
export const isMounted = (state: RootState): boolean => state.users.isAuthStateObserverMounted;
export const getUserAuthState = (state: RootState): UserAuthState => state.users.authState;
export const getIsUserLoggedIn = (state: RootState): boolean => state.users.authState === UserAuthState.LoggedIn;
export const getFbSqUser = (state: RootState): Maybe<FbSqUser> => state.users.user;
// #region USER ***************************************************************
export const getUid = (state: RootState): string => state.users.user.uid;
export const getEmail = (state: RootState): string => state.users.user.email;
export const getName = (state: RootState): string => state.users.user.name;
export const getEmailVerified = (state: RootState): boolean => state.users.user.emailVerified;
export const getIsAnonymousUser = (state: RootState): boolean => state.users.user.isAnonymous;
export const getIsEditor = (state: RootState): boolean => state.users.user.isEditor;
export const getCreatedOn = (state: RootState): number => state.users.user.createdOn;
export const modifiedOn = (state: RootState): number => state.users.user.modifiedOn;
// #endregion

export default sqUserSlice.reducer;
